/**
 * Small numeric helpers shared by grades, awards and lines.
 * Everything here reads ChatStats defensively: a missing or non-finite value
 * means "not enough data", never a number that could leak into the copy.
 */
import type { PersonStats } from '../types';

/** Fewer conversations than this make 선톡 shares meaningless (one chat = one starter). */
export const MIN_CONVERSATIONS = 5;
/** Per-person minimum for rates over all messages (새벽, 표현력). */
export const MIN_MESSAGES_FOR_RATE = 10;
/** Per-person minimum for rates over text messages (웃음, 장문, 단답, 질문). */
export const MIN_TEXTS_FOR_RATE = 10;

/** Tolerance for threshold checks; absorbs float noise such as 0.7 - 0.4 or 11 / 30 * 3. */
export const EPSILON = 1e-9;

// Absolute floors (spec §5.2, §5.3). A superlative such as 칼답 or 장문 needs a
// value that earns the word, not only a lead over the other person.

/** 칼답 우등생 and the '칼답' headline role: a median reply that prints as 10분 or less. */
export const FAST_REPLY_MAX_MINUTES = 10;
/** 장문 작가, the '장문' headline role and the '편지' wording: an average that prints as 15자 or more. */
export const LONG_FORM_MIN_CHARS = 15;
/** ㅋ 장인: at least this many ㅋ. */
export const LAUGH_AWARD_MIN_K = 30;
/** 이모티콘 부자: at least this many emoticon messages. */
export const EMOTICON_AWARD_MIN = 10;
/**
 * 선톡 반장 and the 선톡 grade A: a printed start share at least this many
 * times the even share (55% in a pair, 37% of three, 28% of four). "선톡 50%"
 * against "선톡 50%" is a tie, not a lead.
 */
export const CLEAR_STARTER_RELATIVE = 1.1;
/** 사랑 표현 담당: at least this many ‘사랑해’... */
export const LOVE_AWARD_MIN = 3;
/** ...and this many more than anyone else, so a single message never decides it. */
export const LOVE_AWARD_MIN_LEAD = 2;

/** A start share as the grade reads it: the printed share x people (1 = an even split). */
export function printedRelativeShare(share: number, people: number): number {
  return (percentKey(share) / 100) * people;
}

/**
 * "주로" (mostly) needs an absolute majority as printed (51% or more): the top
 * starter of a big room with 28% opens the most conversations, but not most of them.
 */
export function isMajorityShare(share: number): boolean {
  return isFiniteNumber(share) && percentKey(share) > 50;
}

/** True when a median reply delay reads as 10분 or less. */
export function isFastReply(minutes: number): boolean {
  return isFiniteNumber(minutes) && minutes >= 0 && minuteKey(minutes) <= FAST_REPLY_MAX_MINUTES;
}

/** True when an average text length reads as 15자 or more. */
export function isLongForm(chars: number): boolean {
  return isFiniteNumber(chars) && roundKey(chars) >= LONG_FORM_MIN_CHARS;
}

// Pair contrasts, shared by the contrast lines and the subject comments (which
// must not call a pair "비슷" while one of these lines holds).

/** 답장 시차: one median is at least this many times the other. */
export const REPLY_GAP_RATIO = 5;
/** 장문 대비: one average length is at least this many times the other. */
export const LENGTH_CONTRAST_RATIO = 2;

/** How many times slower the slower median is; a sub-minute median counts as 1분. */
export function replyGapRatio(fast: number, slow: number): number {
  return slow / Math.max(fast, 1);
}

/** long / short, or null when the shorter average is under 1자 (no meaningful ratio). */
export function lengthContrastRatio(long: number, short: number): number | null {
  return short < 1 ? null : long / short;
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function atLeast(value: number, threshold: number): boolean {
  return value >= threshold - EPSILON;
}

export function atMost(value: number, threshold: number): boolean {
  return value <= threshold + EPSILON;
}

/** Where value sits between from and to, clamped to 0-1. Used to scale line weights. */
export function progress(value: number, from: number, to: number): number {
  if (!Number.isFinite(value) || from === to) return 0;
  return Math.min(1, Math.max(0, (value - from) / (to - from)));
}

/** A finite value >= 0, or null. */
export function amount(value: unknown): number | null {
  return isFiniteNumber(value) && value >= 0 ? value : null;
}

/** A finite rate clamped to 0-1, or null. */
export function rate(value: unknown): number | null {
  const valid = amount(value);
  return valid === null ? null : Math.min(valid, 1);
}

export function hasEnoughMessages(person: PersonStats): boolean {
  return (amount(person.messages) ?? 0) >= MIN_MESSAGES_FOR_RATE;
}

export function hasEnoughTexts(person: PersonStats): boolean {
  return (amount(person.textMessages) ?? 0) >= MIN_TEXTS_FOR_RATE;
}

/**
 * Every emoji the person used. Falls back to summing the top list when the
 * total is missing (older fixtures), which can only undercount.
 */
export function emojiUses(person: PersonStats): number {
  const total = amount(person.emojiCount);
  if (total !== null) return total;
  let sum = 0;
  for (const item of person.emojis ?? []) sum += amount(item.count) ?? 0;
  return sum;
}

/** 표현력 value: (emoji uses + emoticon messages) per message. */
export function expressionRate(person: PersonStats): number | null {
  const messages = amount(person.messages);
  if (!messages) return null;
  return (emojiUses(person) + (amount(person.media?.emoticon) ?? 0)) / messages;
}

// ---------------------------------------------------------------------------
// Leaders
// ---------------------------------------------------------------------------

export interface Leader {
  readonly index: number;
  readonly value: number;
  /** The next best value, or null when the leader was the only one eligible. */
  readonly runnerUp: number | null;
}

export interface LeaderOptions {
  readonly direction?: 'max' | 'min';
  /** Compare on what the copy prints, so values that read the same count as a tie. */
  readonly key?: (value: number) => number;
  /** With fewer eligible people there is nobody to compare against. Default 2. */
  readonly minEligible?: number;
}

/**
 * The single best person among eligible values (null = not eligible).
 * Returns null when nobody stands out: too few eligible people or a tie at the top.
 */
export function uniqueLeader(values: readonly (number | null)[], options: LeaderOptions = {}): Leader | null {
  const { direction = 'max', key = (value: number) => value, minEligible = 2 } = options;
  const eligible: { index: number; value: number; key: number }[] = [];
  values.forEach((value, index) => {
    if (isFiniteNumber(value)) eligible.push({ index, value, key: key(value) });
  });
  if (eligible.length === 0 || eligible.length < minEligible) return null;
  const sign = direction === 'max' ? -1 : 1;
  eligible.sort((a, b) => sign * (a.key - b.key) || a.index - b.index);
  const [first, second] = eligible;
  if (!first) return null;
  if (second && second.key === first.key) return null;
  return { index: first.index, value: first.value, runnerUp: second ? second.value : null };
}

/** Percent as printed by formatPercent. */
export function percentKey(value: number): number {
  return Math.round(value * 100);
}

/** Minutes as printed by formatDuration below a day ("1분 미만" = 0). */
export function minuteKey(value: number): number {
  return value < 1 ? 0 : Math.round(value);
}

/** Whole number as printed by formatCount (e.g. "12자"). */
export function roundKey(value: number): number {
  return Math.round(value);
}

/** Count per 100 messages as printed by per100Text ("1개 미만" = 0). */
export function per100Key(value: number): number {
  return Math.round(value * 100);
}
